import React, { useState, useEffect } from 'react';
import { X, Download } from 'lucide-react';
import api from '../services/api';

export const PdfViewer = ({ documentId }: { documentId: number }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let blobUrl: string | null = null;
    const fetchPdf = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/documents/${documentId}/preview`, {
          responseType: 'blob'
        });
        
        // Ensure the response isn't unexpectedly JSON (like a server error)
        if (response.data && response.data.type === 'application/json') {
          setError('Failed to load PDF preview (Permission denied or not found).');
          setLoading(false);
          return;
        }

        blobUrl = URL.createObjectURL(response.data);
        setUrl(blobUrl);
        setLoading(false);
      } catch (err: any) {
        if (err.response && err.response.status === 403) {
            setError('You do not have permission to view this document.');
        } else {
            setError('Failed to load PDF preview.');
        }
        setLoading(false);
      }
    };
    
    fetchPdf();
    
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [documentId]);

  if (error) return <div className="text-red-500 p-4 bg-red-50 rounded-lg border border-red-200">{error}</div>;
  if (loading || !url) return <div className="animate-pulse bg-gray-200 dark:bg-gray-700 h-[600px] rounded-lg flex items-center justify-center text-gray-500 dark:text-gray-400">Loading document securely...</div>;

  return (
    <div className="flex flex-col items-center bg-gray-50 dark:bg-gray-800 rounded-lg w-full h-[600px] border border-gray-200 dark:border-gray-700 overflow-hidden">
      <iframe
        src={`${url}#toolbar=0`}
        className="w-full h-full border-none"
        title="PDF Preview"
      />
    </div>
  );
};

export const TextContentViewer = ({ documentId, fileType }: { documentId: number, fileType: string }) => {
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    const fetchContent = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/documents/${documentId}/content`);
        if (isMounted) {
          setContent(response.data.content);
          setLoading(false);
        }
      } catch (err: any) {
        if (isMounted) {
          setError('Failed to load document content.');
          setLoading(false);
        }
      }
    };
    fetchContent();
    return () => { isMounted = false; };
  }, [documentId]);

  if (error) return <div className="text-red-500 p-4 bg-red-50 rounded-lg border border-red-200">{error}</div>;
  if (loading) return <div className="animate-pulse bg-gray-200 dark:bg-gray-700 h-[600px] rounded-lg flex items-center justify-center text-gray-500 dark:text-gray-400">Loading document text...</div>;
  if (!content || content.trim() === '') return <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-8 h-[600px] flex items-center justify-center text-gray-500 dark:text-gray-400">No text content could be extracted from this document.</div>;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 h-[600px] overflow-y-auto w-full border border-gray-200 dark:border-gray-700">
      {fileType.toLowerCase() === 'txt' ? (
        <pre className="whitespace-pre-wrap font-mono text-sm text-gray-800 dark:text-gray-200 leading-relaxed">
          {content}
        </pre>
      ) : (
        <div className="prose prose-sm max-w-none dark:prose-invert">
          {content.split('\n').map((paragraph, index) => (
            paragraph.trim() !== '' && (
              <p key={index} className="mb-3 text-gray-800 dark:text-gray-200 leading-relaxed">
                {paragraph}
              </p>
            )
          ))}
        </div>
      )}
    </div>
  );
};

interface DocumentPreviewModalProps {
  document: any;
  isOpen: boolean;
  onClose: () => void;
  onDownload: (document: any) => void;
}

export const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
  document,
  isOpen,
  onClose,
  onDownload
}) => {
  if (!isOpen || !document) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {document.title}
          </h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onDownload(document)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <Download className="w-4 h-4 inline mr-2" />
              Download
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Document Metadata */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Type:</span>
              <p className="text-gray-900 dark:text-white">{document.document_type}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Court:</span>
              <p className="text-gray-900 dark:text-white">{document.court || 'N/A'}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Publisher:</span>
              <p className="text-gray-900 dark:text-white">{document.publisher || 'N/A'}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Date:</span>
              <p className="text-gray-900 dark:text-white">
                {document.date_of_document ? new Date(document.date_of_document).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          </div>

          {/* Document Content */}
          <div className="space-y-4">
            {document.summary && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Summary</h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{document.summary}</p>
              </div>
            )}

            {/* Display Document natively using our Viewer */}
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Original Document</h3>
            {(document.file_type && ['pdf', 'doc', 'docx'].includes(document.file_type.toLowerCase())) ? (
              <PdfViewer documentId={document.id} />
            ) : (
              <TextContentViewer documentId={document.id} fileType={document.file_type || 'txt'} />
            )}

            {document.citation && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Citation</h3>
                <p className="text-gray-700 dark:text-gray-300 font-mono">{document.citation}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
